import type { CreditSearchCourse, CreditSearchInstitution, CreditSearchSubject } from './api'

export type CreditSearchPreviewExternalSubject = {
  external_subject_name: string
  external_subject_code: string
  external_subject_level: '0'
  requested_subject: ''
  external_subject_year: '2019'
}

export type CreditSearchPreviewExternalCourse = {
  external_subjects: CreditSearchPreviewExternalSubject[]
  external_institution_id: number | null
  external_institution_name: string | null
  external_course_name: string
  external_course_completed: 0 | 1
}

export type CreditSearchPreviewPayload = {
  external_courses: CreditSearchPreviewExternalCourse[]
  international_student: '0'
  international_student_under_18: '0'
  attached_files: []
  course_data?: {
    course_code: string
    course_campus: CreditSearchCourse['course_campus']
    course_name: string
  }
}

export type CreditSearchPreviewOutcome = {
  subject_code: string
  subject_name: string
}

export type CreditSearchPreviewRule = {
  filtered_outcomes: CreditSearchPreviewOutcome[]
  other_requirements: string
  rule_id: string
}

export type CreditSearchPreviewRequisite = {
  rule: CreditSearchPreviewRule
}

export type CreditSearchPreviewSubject = {
  external_subject_code: string
  subject_code: string
  subject_name: string
  matching_requisites: CreditSearchPreviewRequisite[]
}

export type CreditSearchPreviewSubjectGroup = {
  subjects: Array<{
    subject_code: string
    subject_name: string
  }>
}

export type CreditSearchPreviewArticulationSection = {
  conditions: string
  subject_groups: CreditSearchPreviewSubjectGroup[]
}

export type CreditSearchPreviewArticulationAgreement = {
  reference: string
  articulation_sections: CreditSearchPreviewArticulationSection[]
}

export type CreditSearchPreviewInstitution = {
  id: number
  name: string
}

export type CreditSearchPreviewCourse = {
  external_course_name: string
  institution: CreditSearchPreviewInstitution
  external_course_institution: CreditSearchPreviewInstitution
  subjects: CreditSearchPreviewSubject[]
  articulation_agreements: CreditSearchPreviewArticulationAgreement[]
}

export type CreditSearchPreviewSubmission = {
  reference: string
  course_code: string
  course_name: string
  course_campus: {
    code: string
  }
  external_courses: CreditSearchPreviewCourse[]
}

export type CreditSearchPreviewData = {
  submission: CreditSearchPreviewSubmission
}

export function buildCreditSearchPreviewPayload(
  selectedSubjects: Readonly<Record<string, CreditSearchSubject>>,
  selectedCourses: readonly CreditSearchCourse[],
  selectedInstitution: CreditSearchInstitution | null,
): CreditSearchPreviewPayload {
  const externalCourses: CreditSearchPreviewExternalCourse[] = []
  const subjectCourseIndexes = new Map<number, number>()

  for (const subject of Object.values(selectedSubjects)) {
    let courseIndex = subjectCourseIndexes.get(subject.institution_id)
    if (courseIndex === undefined) {
      courseIndex = externalCourses.length
      subjectCourseIndexes.set(subject.institution_id, courseIndex)
      externalCourses.push({
        external_subjects: [],
        external_institution_id: subject.institution_id,
        external_institution_name: subject.institution_name,
        external_course_name: '',
        external_course_completed: 0,
      })
    }

    externalCourses[courseIndex]?.external_subjects.push({
      external_subject_name: subject.subject_name,
      external_subject_code: subject.subject_code,
      external_subject_level: '0',
      requested_subject: '',
      external_subject_year: '2019',
    })
  }

  const selectedCourse = selectedCourses[0]
  if (selectedCourse) {
    externalCourses.push({
      external_subjects: [],
      external_institution_id: selectedCourse.institution_id ?? selectedInstitution?.id ?? null,
      external_institution_name: selectedCourse.institution_id === null ? null : selectedCourse.institution_name,
      external_course_name: selectedCourse.external_course_name,
      external_course_completed: 1,
    })
  }

  const payload: CreditSearchPreviewPayload = {
    external_courses: externalCourses,
    international_student: '0',
    international_student_under_18: '0',
    attached_files: [],
  }

  if (selectedCourse) {
    payload.course_data = {
      course_code: selectedCourse.course_code,
      course_campus: selectedCourse.course_campus,
      course_name: selectedCourse.course_name,
    }
  }

  return payload
}
